import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { GridColDef, GridRowModel } from "@mui/x-data-grid";

import {
  ClassroomsNoNulls,
  fetchDeleteClassrooms,
  fetchDeleteClassroomsAll,
  fetchGetClassroomsAll,
  fetchPostClassrooms,
  fetchPutClassroom,
  PSDataGrid,
  SetSnackType,
  SettingsGrades,
  TeachersNoNulls,
  Upload,
  UploadOverwriteDialog,
} from "../../utils";

const DoDelete = () => {
  return useCallback(
    (classroom: ClassroomsNoNulls) =>
      new Promise<ClassroomsNoNulls>(async (resolve, reject) => {
        try {
          const result = (await fetchDeleteClassrooms([classroom])) as any;
          if (Object.keys(result).includes("error")) throw result;
          resolve(result as ClassroomsNoNulls);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPost = () => {
  return useCallback(
    (classroom: ClassroomsNoNulls) =>
      new Promise<ClassroomsNoNulls>(async (resolve, reject) => {
        try {
          const result = (await fetchPostClassrooms([classroom as ClassroomsNoNulls])) as ClassroomsNoNulls;
          resolve(result);
        } catch (err: any) {
          reject(err);
        }
      }),
    [],
  );
};

const DoPut = () => {
  return useCallback(
    (classroom: ClassroomsNoNulls) =>
      new Promise<ClassroomsNoNulls>(async (resolve, reject) => {
        if (classroom.name?.trim() === "") {
          reject(new Error("Error while saving: name can't be empty."));
        } else {
          try {
            const updatedDoc = (await fetchPutClassroom(classroom)) as ClassroomsNoNulls;
            resolve(updatedDoc);
          } catch (err: any) {
            reject(err);
          }
        }
      }),
    [],
  );
};

export const Classrooms = (props: {
  disconnect: () => void;
  setSnack: SetSnackType;
  settingsGrades: SettingsGrades;
  teachers: TeachersNoNulls[];
}) => {
  const [rows, setRows] = useState(Array<ClassroomsNoNulls>());
  const [rowsLoading, setRowsLoading] = useState(false);
  const [actionDeleteVisible, setActionDeleteVisible] = useState(false);

  const [addClassroomDialogOpen, setAddClassroomDialogOpen] = useState(false);
  const [addClassroomDialogCancelOpen, setClassroomAddDialogCancelOpen] = useState(false);
  const [addClassroomName, setAddClassroomName] = useState("");
  const [addClassroomTeachersOpen, setAddClassroomTeachersOpen] = useState(false);
  const [addClassroomTeacherId, setAddClassroomTeacherId] = useState(0);
  const [addClassroomGrade0am, setAddClassroomGrade0am] = useState(0);
  const [addClassroomGrade0pm, setAddClassroomGrade0pm] = useState(0);
  const [addClassroomGrade1, setAddClassroomGrade1] = useState(0);
  const [addClassroomGrade2, setAddClassroomGrade2] = useState(0);
  const [addClassroomGrade3, setAddClassroomGrade3] = useState(0);
  const [addClassroomGrade4, setAddClassroomGrade4] = useState(0);
  const [addClassroomGrade5, setAddClassroomGrade5] = useState(0);
  const [addClassroomGrade6, setAddClassroomGrade6] = useState(0);
  const [addClassroomGrade7, setAddClassroomGrade7] = useState(0);
  const [addClassroomGrade8, setAddClassroomGrade8] = useState(0);
  const [addClassroomGrade9, setAddClassroomGrade9] = useState(0);
  const [addClassroomGrade10, setAddClassroomGrade10] = useState(0);
  const [addClassroomGrade11, setAddClassroomGrade11] = useState(0);
  const [addClassroomGrade12, setAddClassroomGrade12] = useState(0);
  const [addClassroomGrade13, setAddClassroomGrade13] = useState(0);

  const [addClassroomNameError, setAddClassroomNameError] = useState(false);
  const [addClassroomTeacherError, setAddClassroomTeacherError] = useState(false);
  const [addClassroomGrade0amError, setAddClassroomGrade0amError] = useState(false);
  const [addClassroomGrade0pmError, setAddClassroomGrade0pmError] = useState(false);
  const [addClassroomGrade1Error, setAddClassroomGrade1Error] = useState(false);
  const [addClassroomGrade2Error, setAddClassroomGrade2Error] = useState(false);
  const [addClassroomGrade3Error, setAddClassroomGrade3Error] = useState(false);
  const [addClassroomGrade4Error, setAddClassroomGrade4Error] = useState(false);
  const [addClassroomGrade5Error, setAddClassroomGrade5Error] = useState(false);
  const [addClassroomGrade6Error, setAddClassroomGrade6Error] = useState(false);
  const [addClassroomGrade7Error, setAddClassroomGrade7Error] = useState(false);
  const [addClassroomGrade8Error, setAddClassroomGrade8Error] = useState(false);
  const [addClassroomGrade9Error, setAddClassroomGrade9Error] = useState(false);
  const [addClassroomGrade10Error, setAddClassroomGrade10Error] = useState(false);
  const [addClassroomGrade11Error, setAddClassroomGrade11Error] = useState(false);
  const [addClassroomGrade12Error, setAddClassroomGrade12Error] = useState(false);
  const [addClassroomGrade13Error, setAddClassroomGrade13Error] = useState(false);

  const [uploadPendingRows, setUploadPendingRows] = useState(null as Upload[] | null);
  const [openUploadOverwrite, setOpenUploadOverwrite] = useState(false);

  const addRow = DoPost();
  const removeRow = DoDelete();
  const mutateRow = DoPut();

  useEffect(() => {
    (async () => {
      setRowsLoading(true);
      const result = await fetchGetClassroomsAll();
      if (Object.keys(result).includes("error")) {
        props.disconnect();
        props.setSnack((result as any).error?.toString(), "error");
      } else {
        setRows(result as ClassroomsNoNulls[]);
      }
      setRowsLoading(false);
    })();
  }, []);

  const resetAddClassroom = () => {
    setAddClassroomName("");
    setAddClassroomTeacherId(0);
    setAddClassroomGrade0am(0);
    setAddClassroomGrade0pm(0);
    setAddClassroomGrade1(0);
    setAddClassroomGrade2(0);
    setAddClassroomGrade3(0);
    setAddClassroomGrade4(0);
    setAddClassroomGrade5(0);
    setAddClassroomGrade6(0);
    setAddClassroomGrade7(0);
    setAddClassroomGrade8(0);
    setAddClassroomGrade9(0);
    setAddClassroomGrade10(0);
    setAddClassroomGrade11(0);
    setAddClassroomGrade12(0);
    setAddClassroomGrade13(0);
  };

  const resetAddClassroomErrors = () => {
    setAddClassroomNameError(false);
    setAddClassroomTeacherError(false);
    setAddClassroomGrade0amError(false);
    setAddClassroomGrade0pmError(false);
    setAddClassroomGrade1Error(false);
    setAddClassroomGrade2Error(false);
    setAddClassroomGrade3Error(false);
    setAddClassroomGrade4Error(false);
    setAddClassroomGrade5Error(false);
    setAddClassroomGrade6Error(false);
    setAddClassroomGrade7Error(false);
    setAddClassroomGrade8Error(false);
    setAddClassroomGrade9Error(false);
    setAddClassroomGrade10Error(false);
    setAddClassroomGrade11Error(false);
    setAddClassroomGrade12Error(false);
    setAddClassroomGrade13Error(false);
  };

  const validateAddClassroom = (classroomToAdd: ClassroomsNoNulls) => {
    resetAddClassroomErrors();
    let isValid = true;
    if (!classroomToAdd.name) {
      setAddClassroomNameError(true);
      isValid = false;
    }
    if (!classroomToAdd.teacher_id) {
      setAddClassroomTeacherError(true);
      isValid = false;
    }
    if ((classroomToAdd.seats0am ?? -1) < 0) {
      setAddClassroomGrade0amError(true);
      isValid = false;
    }
    if ((classroomToAdd.seats0pm ?? -1) < 0) {
      setAddClassroomGrade0pmError(true);
      isValid = false;
    }
    if ((classroomToAdd.seats1 ?? -1) < 0) {
      setAddClassroomGrade1Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats2 ?? -1) < 0) {
      setAddClassroomGrade2Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats3 ?? -1) < 0) {
      setAddClassroomGrade3Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats4 ?? -1) < 0) {
      setAddClassroomGrade4Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats5 ?? -1) < 0) {
      setAddClassroomGrade5Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats6 ?? -1) < 0) {
      setAddClassroomGrade6Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats7 ?? -1) < 0) {
      setAddClassroomGrade7Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats8 ?? -1) < 0) {
      setAddClassroomGrade8Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats9 ?? -1) < 0) {
      setAddClassroomGrade9Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats10 ?? -1) < 0) {
      setAddClassroomGrade10Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats11 ?? -1) < 0) {
      setAddClassroomGrade11Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats12 ?? -1) < 0) {
      setAddClassroomGrade12Error(true);
      isValid = false;
    }
    if ((classroomToAdd.seats13 ?? -1) < 0) {
      setAddClassroomGrade13Error(true);
      isValid = false;
    }
    if (!isValid) {
      console.error("classroomToAdd:", classroomToAdd);
      props.setSnack("Unable to add classroom, check invalid fields", "warning");
    }
    return isValid;
  };

  const processRowAdd = useCallback(
    async (classroom: ClassroomsNoNulls) => {
      setRowsLoading(true);
      try {
        const res = (await addRow(classroom)) as any;
        if (Object.keys(res).includes("error")) throw res;
        const classroomAdded = res as ClassroomsNoNulls[][0];
        setAddClassroomDialogOpen(false);
        setRows(r => [...r, classroomAdded]);
        props.setSnack("Classroom successfully added", "success");
        resetAddClassroom();
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err.error}: ${err.existing.name}`, "error");
      } finally {
        setRowsLoading(false);
      }
    },
    [props, addRow],
  );

  const processRowsAdds = useCallback(
    async (classrooms: ClassroomsNoNulls[]) => {
      setRowsLoading(true);
      try {
        const res = (await fetchPostClassrooms(classrooms)) as any;
        if (Object.keys(res).includes("error") || res.acknowledged === false) throw res;
        props.setSnack(`Successfully added ${res.length} Classrooms`, "success");
        setRows(r => [...r, ...(res as ClassroomsNoNulls[])]);
      } catch (err: any) {
        console.error(err);
        props.setSnack(
          err.error.includes('error: duplicate key value violates unique constraint "')
            ? "Failed: duplicates were present"
            : (err.error ?? err.toString()),
          "error",
        );
      } finally {
        setRowsLoading(false);
      }
    },
    [props],
  );

  const processRowRemove = useCallback(
    async (classroom: ClassroomsNoNulls) => {
      setRowsLoading(true);
      try {
        const res = (await removeRow(classroom)) as any;
        if (Object.keys(res).includes("error")) throw res.error;
        console.log(res);
        setRows(r => r.filter(i => i.name !== classroom.name));
        props.setSnack("Classroom successfully deleted", "success");
      } catch (err: any) {
        console.error(err);
        props.setSnack(err.error ?? err.toString(), "error");
      } finally {
        setActionDeleteVisible(false);
        setRowsLoading(false);
      }
    },
    [props, removeRow],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      let diff = false;
      for (const k of Object.keys(newRow)) {
        if (newRow[k] !== oldRow[k]) {
          diff = true;
          break;
        }
      }
      if (!diff) {
        return oldRow;
      }

      setRowsLoading(true);
      try {
        const { teacher: updateToTeacher, ...rest } = newRow;
        const res = await mutateRow({
          ...rest,
          teacher_id: updateToTeacher ?? oldRow.teacher_id,
        } as ClassroomsNoNulls);
        const { teacher_id, ...classroom } = res;
        props.setSnack("Classroom successfully updated", "success");
        setRows(r => r.map(iRow => (iRow.name === res.name ? { ...classroom, teacher_id } : iRow)));
        return res;
      } catch (err: any) {
        console.error(err);
        props.setSnack(`${err.error}`, "error");
      } finally {
        setRowsLoading(false);
      }
    },
    [props, mutateRow],
  );

  const handleProcessRowUpdateError = useCallback(
    (error: Error) => {
      props.setSnack(error.message, "error");
    },
    [props],
  );

  const performUpload = async (overwrite: boolean) => {
    try {
      if (overwrite) {
        const dropped = await fetchDeleteClassroomsAll();
        console.log(dropped);
        setRows([]);
      }
      await processRowsAdds(uploadPendingRows as ClassroomsNoNulls[]);
    } catch (err: any) {
      console.error(err);
    } finally {
      setUploadPendingRows(null);
      setOpenUploadOverwrite(false);
    }
  };

  const uploadClassrooms = (uploadedRows: Upload[]) => {
    setUploadPendingRows(uploadedRows);
  };

  useEffect(() => {
    if (!uploadPendingRows) return;
    if (rows.length > 0) {
      setOpenUploadOverwrite(true);
    } else {
      performUpload(false);
    }
  }, [uploadPendingRows]);

  const columns: GridColDef[] = [
    {
      field: "actionDelete",
      headerAlign: "center",
      headerName: "Delete",
      width: 70,
      minWidth: 70,
      align: "center",
      sortable: false,
      renderCell: params => (
        <Button startIcon={<DeleteIcon />} onClick={ev => processRowRemove(params.row as ClassroomsNoNulls)} />
      ),
    },
    { field: "name", headerName: "Classroom Name", flex: 1, maxWidth: 300, editable: true },
    {
      field: "teacher_id",
      cellClassName: "with-autocomplete",
      headerName: "Teacher",
      flex: 1,
      maxWidth: 250,
      editable: true,
      renderCell: params => (
        <Autocomplete
          blurOnSelect
          disableClearable
          fullWidth
          getOptionLabel={(t: TeachersNoNulls) =>
            !t?.id ? "" : `${t.prefix === "N/A" ? t.first : t.prefix} ${t.last}`
          }
          id={params.row.id}
          isOptionEqualToValue={(opt: TeachersNoNulls, val: TeachersNoNulls) => opt.id === val.id}
          loading={rowsLoading}
          onChange={(ev: any, val: TeachersNoNulls) => {
            processRowUpdate({ ...params.row, teacher: val.id }, params.row); // todo: fix flicker of `processRowUpdate`'s oldRow before `setRows` updates state to newRow
            params.cellMode = "view"; // todo: this doesn't blur when clicking "x" to remove (though does invoke DEL)
          }}
          options={props.teachers.filter(
            t =>
              (t.grade0am && params.row.seats0am > 0) ||
              (t.grade0pm && params.row.seats0pm > 0) ||
              (t.grade1 && params.row.seats1 > 0) ||
              (t.grade2 && params.row.seats2 > 0) ||
              (t.grade3 && params.row.seats3 > 0) ||
              (t.grade4 && params.row.seats4 > 0) ||
              (t.grade5 && params.row.seats5 > 0) ||
              (t.grade6 && params.row.seats6 > 0) ||
              (t.grade7 && params.row.seats7 > 0) ||
              (t.grade8 && params.row.seats8 > 0) ||
              (t.grade9 && params.row.seats9 > 0) ||
              (t.grade10 && params.row.seats10 > 0) ||
              (t.grade11 && params.row.seats11 > 0) ||
              (t.grade12 && params.row.seats12 > 0) ||
              (t.grade13 && params.row.seats13 > 0),
          )}
          renderInput={p => <TextField {...p} label="" size="small" variant="standard" />}
          size="small"
          slotProps={{
            popper: {
              sx: { my: 0, py: 0 },
            },
            paper: {
              sx: { fontSize: "0.875rem" },
            },
          }}
          sx={{
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
            },
            "& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover": {
              borderBottom: "none",
            },
          }}
          value={props.teachers.find(t => t.id === params.row.teacher_id) ?? ({} as TeachersNoNulls)}
        />
      ),
      valueFormatter: (value: TeachersNoNulls) =>
        !value ? "" : `${value?.prefix === "N/A" ? value?.first : value?.prefix} ${value?.last}`,
    },
    ...((props.settingsGrades["ECS-AM"]
      ? [
          {
            field: "seats0am",
            type: "number",
            headerAlign: "center",
            headerName: "ECS-AM",
            align: "center",
            width: 80,
            minWidth: 80,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["ECS-PM"]
      ? [
          {
            field: "seats0pm",
            type: "number",
            headerAlign: "center",
            headerName: "ECS-PM",
            align: "center",
            width: 80,
            minWidth: 80,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 1"]
      ? [
          {
            field: "seats1",
            type: "number",
            headerAlign: "center",
            headerName: "1",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 2"]
      ? [
          {
            field: "seats2",
            type: "number",
            headerAlign: "center",
            headerName: "2",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 3"]
      ? [
          {
            field: "seats3",
            type: "number",
            headerAlign: "center",
            headerName: "3",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 4"]
      ? [
          {
            field: "seats4",
            type: "number",
            headerAlign: "center",
            headerName: "4",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 5"]
      ? [
          {
            field: "seats5",
            type: "number",
            headerAlign: "center",
            headerName: "5",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 6"]
      ? [
          {
            field: "seats6",
            type: "number",
            headerAlign: "center",
            headerName: "6",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 7"]
      ? [
          {
            field: "seats7",
            type: "number",
            headerAlign: "center",
            headerName: "7",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 8"]
      ? [
          {
            field: "seats8",
            type: "number",
            headerAlign: "center",
            headerName: "8",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 9"]
      ? [
          {
            field: "seats9",
            type: "number",
            headerAlign: "center",
            headerName: "9",
            align: "center",
            width: 50,
            minWidth: 50,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 10"]
      ? [
          {
            field: "seats10",
            type: "number",
            headerAlign: "center",
            headerName: "10",
            align: "center",
            width: 40,
            minWidth: 40,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 11"]
      ? [
          {
            field: "seats11",
            type: "number",
            headerAlign: "center",
            headerName: "11",
            align: "center",
            width: 40,
            minWidth: 40,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 12"]
      ? [
          {
            field: "seats12",
            type: "number",
            headerAlign: "center",
            headerName: "12",
            align: "center",
            width: 40,
            minWidth: 40,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
    ...((props.settingsGrades["Gr 13"]
      ? [
          {
            field: "seats13",
            type: "number",
            headerAlign: "center",
            headerName: "13",
            align: "center",
            width: 40,
            minWidth: 40,
            editable: true,
            valueGetter: (value: number) => value ?? 0,
          },
        ]
      : []) as GridColDef[]),
  ];

  return (
    <>
      <PSDataGrid
        actionDeleteVisible={actionDeleteVisible}
        columns={columns}
        initialSortField="name"
        loading={rowsLoading}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        processRowUpdate={processRowUpdate}
        psType={"Classrooms"}
        rows={rows}
        setActionDeleteVisible={setActionDeleteVisible}
        setAddDialogOpen={setAddClassroomDialogOpen}
        setSnack={props.setSnack}
        teachers={props.teachers}
        toolbarHeaderRow={
          <Stack direction="row">
            <Box sx={{ width: "30%" }}></Box>
            <Box
              sx={{
                display: "flex",
                width: "70%",
                maxWidth: 700,
                justifyContent: "center",
                fontWeight: "bold",
              }}
            >
              Seats of Grade:
            </Box>
          </Stack>
        }
        upload={uploadClassrooms}
      />
      <Dialog open={addClassroomDialogOpen}>
        <DialogTitle>Add Classroom</DialogTitle>
        <DialogContent>
          <DialogContentText>Asterisks indicate required fields.</DialogContentText>
          <TextField
            autoFocus
            error={addClassroomNameError}
            fullWidth
            id="add-name"
            label="Classroom Name"
            margin="dense"
            required
            type="text"
            variant="standard"
            onChange={ev => {
              setAddClassroomNameError(false);
              setAddClassroomName(ev.target.value ?? "");
            }}
          />
          {props.settingsGrades["ECS-AM"] ? (
            <TextField
              error={addClassroomGrade0amError}
              fullWidth
              id="add-grade0am"
              label="ECS-AM Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade0amError(false);
                setAddClassroomGrade0am(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["ECS-PM"] ? (
            <TextField
              error={addClassroomGrade0pmError}
              fullWidth
              id="add-grade0pm"
              label="ECS-PM Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade0pmError(false);
                setAddClassroomGrade0pm(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 1"] ? (
            <TextField
              error={addClassroomGrade1Error}
              fullWidth
              id="add-grade1"
              label="Grade 1 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade1Error(false);
                setAddClassroomGrade1(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 2"] ? (
            <TextField
              error={addClassroomGrade2Error}
              fullWidth
              id="add-grade2"
              label="Grade 2 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade2Error(false);
                setAddClassroomGrade2(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 3"] ? (
            <TextField
              error={addClassroomGrade3Error}
              fullWidth
              id="add-grade3"
              label="Grade 3 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade3Error(false);
                setAddClassroomGrade3(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 4"] ? (
            <TextField
              error={addClassroomGrade4Error}
              fullWidth
              id="add-grade4"
              label="Grade 4 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade4Error(false);
                setAddClassroomGrade4(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 5"] ? (
            <TextField
              error={addClassroomGrade5Error}
              fullWidth
              id="add-grade5"
              label="Grade 5 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade5Error(false);
                setAddClassroomGrade5(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 6"] ? (
            <TextField
              error={addClassroomGrade6Error}
              fullWidth
              id="add-grade6"
              label="Grade 6 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade6Error(false);
                setAddClassroomGrade6(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 7"] ? (
            <TextField
              error={addClassroomGrade7Error}
              fullWidth
              id="add-grade7"
              label="Grade 7 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade7Error(false);
                setAddClassroomGrade7(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 8"] ? (
            <TextField
              error={addClassroomGrade8Error}
              fullWidth
              id="add-grade8"
              label="Grade 8 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade8Error(false);
                setAddClassroomGrade8(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 9"] ? (
            <TextField
              error={addClassroomGrade9Error}
              fullWidth
              id="add-grade9"
              label="Grade 9 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade9Error(false);
                setAddClassroomGrade9(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 10"] ? (
            <TextField
              error={addClassroomGrade10Error}
              fullWidth
              id="add-grade10"
              label="Grade 10 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade10Error(false);
                setAddClassroomGrade10(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 11"] ? (
            <TextField
              error={addClassroomGrade11Error}
              fullWidth
              id="add-grade11"
              label="Grade 11 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade11Error(false);
                setAddClassroomGrade11(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 12"] ? (
            <TextField
              error={addClassroomGrade12Error}
              fullWidth
              id="add-grade12"
              label="Grade 12 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade12Error(false);
                setAddClassroomGrade12(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.settingsGrades["Gr 13"] ? (
            <TextField
              error={addClassroomGrade13Error}
              fullWidth
              id="add-grade13"
              label="Grade 13 Seats"
              margin="dense"
              onChange={ev => {
                setAddClassroomGrade13Error(false);
                setAddClassroomGrade13(parseInt(ev.target.value ?? "-1"));
              }}
              onWheel={ev => (ev.target as HTMLInputElement).blur()}
              type="number"
              variant="standard"
            />
          ) : (
            <></>
          )}
          <Autocomplete
            blurOnSelect
            fullWidth
            getOptionLabel={(t: TeachersNoNulls) => `${t.prefix === "N/A" ? t.first : t.prefix} ${t.last}`}
            id="add-teacher"
            isOptionEqualToValue={(opt: TeachersNoNulls, val: TeachersNoNulls) => opt.id === val.id}
            loading={false}
            onChange={(ev: any, val: any) => {
              setAddClassroomTeacherError(false);
              setAddClassroomTeacherId((val as TeachersNoNulls)?.id);
            }}
            onClose={() => setAddClassroomTeachersOpen(false)}
            onOpen={() => setAddClassroomTeachersOpen(true)}
            open={addClassroomTeachersOpen}
            options={props.teachers.filter(
              t =>
                (t.grade0am && addClassroomGrade0am > 0) ||
                (t.grade0pm && addClassroomGrade0pm > 0) ||
                (t.grade1 && addClassroomGrade1 > 0) ||
                (t.grade2 && addClassroomGrade2 > 0) ||
                (t.grade3 && addClassroomGrade3 > 0) ||
                (t.grade4 && addClassroomGrade4 > 0) ||
                (t.grade5 && addClassroomGrade5 > 0) ||
                (t.grade6 && addClassroomGrade6 > 0) ||
                (t.grade7 && addClassroomGrade7 > 0) ||
                (t.grade8 && addClassroomGrade8 > 0) ||
                (t.grade9 && addClassroomGrade9 > 0) ||
                (t.grade10 && addClassroomGrade10 > 0) ||
                (t.grade11 && addClassroomGrade11 > 0) ||
                (t.grade12 && addClassroomGrade12 > 0) ||
                (t.grade13 && addClassroomGrade13 > 0),
            )}
            renderInput={params => (
              <TextField {...params} error={addClassroomTeacherError} label="Teacher" required sx={{ mt: 2 }} />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (
                (addClassroomName ?? "") === "" &&
                (addClassroomTeacherId ?? 0) === 0 &&
                (addClassroomGrade0am ?? 0) <= 0 &&
                (addClassroomGrade0pm ?? 0) <= 0 &&
                (addClassroomGrade1 ?? 0) <= 0 &&
                (addClassroomGrade2 ?? 0) <= 0 &&
                (addClassroomGrade3 ?? 0) <= 0 &&
                (addClassroomGrade4 ?? 0) <= 0 &&
                (addClassroomGrade5 ?? 0) <= 0 &&
                (addClassroomGrade6 ?? 0) <= 0 &&
                (addClassroomGrade7 ?? 0) <= 0 &&
                (addClassroomGrade8 ?? 0) <= 0 &&
                (addClassroomGrade9 ?? 0) <= 0 &&
                (addClassroomGrade10 ?? 0) <= 0 &&
                (addClassroomGrade11 ?? 0) <= 0 &&
                (addClassroomGrade12 ?? 0) <= 0 &&
                (addClassroomGrade13 ?? 0) <= 0
              ) {
                setAddClassroomDialogOpen(false);
                resetAddClassroomErrors();
              } else {
                setClassroomAddDialogCancelOpen(true);
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              const classroomToAdd = {
                name: addClassroomName,
                seats0am: addClassroomGrade0am,
                seats0pm: addClassroomGrade0pm,
                seats1: addClassroomGrade1,
                seats2: addClassroomGrade2,
                seats3: addClassroomGrade3,
                seats4: addClassroomGrade4,
                seats5: addClassroomGrade5,
                seats6: addClassroomGrade6,
                seats7: addClassroomGrade7,
                seats8: addClassroomGrade8,
                seats9: addClassroomGrade9,
                seats10: addClassroomGrade10,
                seats11: addClassroomGrade11,
                seats12: addClassroomGrade12,
                seats13: addClassroomGrade13,
                teacher_id: addClassroomTeacherId,
              } as ClassroomsNoNulls;
              if (!validateAddClassroom(classroomToAdd)) return;
              processRowAdd(classroomToAdd);
            }}
            variant="contained"
          >
            Add Classroom
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addClassroomDialogCancelOpen} onClose={() => setClassroomAddDialogCancelOpen(false)}>
        <DialogContent>
          <DialogTitle>Cancel anyway?</DialogTitle>
          <DialogContentText>If you cancel now, you'll lose all current data entered.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClassroomAddDialogCancelOpen(false)}>Continue editing</Button>
          <Button
            onClick={() => {
              resetAddClassroom();
              resetAddClassroomErrors();
              setClassroomAddDialogCancelOpen(false);
              setAddClassroomDialogOpen(false);
            }}
            variant="contained"
          >
            Discard & Close
          </Button>
        </DialogActions>
      </Dialog>
      <UploadOverwriteDialog
        open={openUploadOverwrite}
        upload={performUpload}
        uploadCancel={() => {
          setUploadPendingRows(null);
          setOpenUploadOverwrite(false);
        }}
      />
    </>
  );
};
